<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_BUSINESS_AUTOMATION')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="shortagesAccountRef"
      :busy="isBusy"
      :items="cashSuspenseAccountList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_BUSINESS_AUTOMATION')"
              @click="onSelectCashSuspense(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectCashSuspense(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onDeleteCashSuspense(data.item, $event.target)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50"> {{ $t('Delete') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <!-- Pagination -->
    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      :ok-disabled="isLoading"
      size="md"
      :title="modalTitle"
      @hidden="resetModal"
      @ok="handleSubmit"
    >
      <validation-observer ref="cashSuspenseRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Branch -->
            <b-col md="12">
              <SelectField
                id="branch"
                v-model="shortagesAccountFormData.branchCode"
                :label="$t('Branch')"
                :options="branchOptions"
                :reduce="option => option.value"
                :error-name="$t('Branch')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Branch -->

            <!-- Account -->
            <b-col md="12">
              <SelectField
                id="account"
                v-model="shortagesAccountFormData.accountNumber"
                :label="$t('Account')"
                :options="accountTypeOptions"
                :reduce="option => option.value"
                :error-name="$t('Account')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Account -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="md"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCashSuspense"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Branch -->
        <b-col md="12">
          <SelectField
            id="branch"
            v-model="filter.branchID"
            :label="$t('Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Branch -->

        <!-- Account -->
        <b-col md="12">
          <SelectField
            id="account"
            v-model="filter.accountNumber"
            :label="$t('Account')"
            :options="accountTypeOptions"
            :reduce="option => option.value"
            item="label"
            :placeholder="$t('Select')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Account -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BModal, BSpinner,
  BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { toAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import SelectField from '../../../forms/SelectField.vue'
import Button from '../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    TablePagination,
    TableEntries,
    Button,
    BTable,
    BSpinner,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    SelectField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      sortBy: 'branch',
      isBusy: true,
      buttonLabel: this.$t('Save'),
      modalTitle: this.$t('Create Cash Suspense Account'),
      isReadonly: false,
      required,
      isRequired: false,
      isLoading: false,
      sortDesc: false,
      toAmount,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'branchName', sortable: true, label: 'Branch' },
        { key: 'actions', label: 'Actions' },
      ],
      accountTypeOptions: [],
      shortagesAccountFormData: {
        branchCode: '',
        accountNumber: '',
      },
      cashVolumeOptions: [
        { label: 'Unrequired', value: 1 },
        { label: 'Required', value: 2 },
      ],
      cashSuspenseAccountList: [],
      tellerNameOptions: [],
      filter: {
        branchID: '',
        accountNumber: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  mounted() {
    this.getAccounts()
    this.fetchCashSuspense()
  },
  methods: {
    ...mapActions({
      getCashSuspenseList: 'operations/getCashSuspenseList',
      createCashSuspense: 'operations/createCashSuspenseAction',
      updateCashSuspense: 'operations/updateCashSuspenseAction',
      getAccountCategoryByCode: 'account/getAccountCategoryByCode',
      deleteCashSuspense: 'operations/deleteBusinessAutomation',
    }),

    onSelectCashSuspense(items, activity, val) {
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Cash Suspense Account') : this.$t('Cash Suspense Account')
      this.shortagesAccountFormData = { ...items }
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },

    async cashSuspenseAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save' ? await this.createCashSuspense(this.shortagesAccountFormData) : await this.updateCashSuspense(this.shortagesAccountFormData)
        const { status } = response
        if (status === 200) {
          this.fetchCashSuspense()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: this.$t('Cash Suspense Account created successfully') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.cashSuspenseRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisCash = this.$t('this Cash Suspense Account?')
          this.confirm(this.cashSuspenseAction, { text: `${areYouSure} ${this.buttonLabel} ${thisCash}` })
        }
      })
    },

    async fetchCashSuspense() {
      try {
        this.isBusy = true
        const response = await this.getCashSuspenseList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.cashSuspenseAccountList = data.data
          this.totalCount = data.recordCount
        } else {
          this.cashSuspenseAccountList = []
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async getAccounts() {
      try {
        const response = await this.getAccountCategoryByCode(10103000)
        const { data, status } = response
        if (status === 200 && data.data) {
          this.accountTypeOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: item.name,
            value: item.code.toString(),
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async onDeleteCashSuspense(item) {
      const deleteCashSuspense = async () => {
        try {
          const response = await this.deleteCashSuspense({ routeName: 'CashSuspenseAccount', id: item.id })
          const { status } = response
          if (status === 200) {
            this.alertSuccess({ text: this.$t('Deleted successfully') })
          }
        } catch (error) {
          this.alertError({ text: error })
        }
      }
      this.confirm(deleteCashSuspense, { text: this.$t('Are you sure you want to delete this Cash Suspense Account?') })
    },
    resetModal() {
      this.shortagesAccountFormData = { ...null }
      this.isLoading = false
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCashSuspense()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
